package it.neckar.runtime.context

/**
 * Axis 3: Concrete instance/domain.
 *
 * The runtime identity of a service host: its hostname. Product-specific implementations add the
 * URLs / data their app needs (see [it.neckar.projects.products.mea.hosts.MeaHost],
 * [it.neckar.projects.products.elektromeister.hosts.ElektroMeisterHost] in build-logic/descriptors);
 * the machines we operate are `Fleet`, which carries nothing beyond the hostname.
 *
 * These types are published by the descriptors klib: consumers (including JS/browser code) declare
 * `api` on `internal/open/commons/descriptors`.
 *
 * @see ServiceHostRegistry for hostname-based lookup
 */
interface ServiceHost {
  /**
   * The hostname of the service.
   */
  val hostname: Hostname

  /**
   * The default service host for standalone commands with no product-specific host type:
   * a single host at [Hostname.localhost] that is also its own trivial registry. Used as the
   * `serviceHostRegistry` for local CLI tools and as the initial runtime-context host.
   */
  object Default : ServiceHost, FixedHostRegistry<Default> {
    override val hostname: Hostname
      get() = Hostname.localhost

    override val knownHosts: List<Default> = listOf(this)

    override fun resolveHost(hostname: Hostname?): Default {
      return this
    }
  }

  companion object {
    /**
     * Creates a [ServiceHost] identified solely by its [hostname], with no product-specific host
     * type. For a hostname that arrives as text at runtime — a secrets directory name, a
     * configured deploy target. A build script names a `Fleet` constant instead, which
     * `verifyFleet` enforces.
     */
    fun of(hostname: Hostname): ServiceHost = HostnameServiceHost(hostname)
  }
}

/**
 * Implementation backing [ServiceHost.of]: a [ServiceHost] defined by nothing but its [hostname].
 */
private data class HostnameServiceHost(
  override val hostname: Hostname,
) : ServiceHost {
  override fun toString(): String {
    return "ServiceHost(hostname='$hostname')"
  }
}
